import { Component, type ErrorInfo, type ReactNode } from 'react';
import { noticeError } from '../lib/telemetry';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
}

/**
 * ErrorBoundary — catches unhandled React render errors and reports them
 * to New Relic via noticeError(). Renders a plain fallback so the app
 * doesn't go blank.
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    noticeError(error, {
      componentStack: (info.componentStack ?? '').slice(0, 500),
    });
  }

  render(): ReactNode {
    if (this.state.hasError) {
      return (
        <div role="alert" style={{ padding: '2rem', textAlign: 'center' }}>
          <p>Something went wrong. Please refresh the page to try again.</p>
        </div>
      );
    }
    return this.props.children;
  }
}
